import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { useOrganizationStore } from '../store/organizationStore';
import { useToast } from '../contexts/ToastContext';
import { usePageTitle } from '../hooks/usePageTitle';
import LoadingSpinner from '../components/LoadingSpinner';

// Declare Paddle global injected by paddle.js v2
declare global {
  interface Window {
    Paddle?: {
      Initialize: (options: { token: string; eventCallback?: (event: any) => void }) => void;
      Environment: { set: (env: string) => void };
      Checkout: {
        open: (options: {
          transactionId: string;
          settings?: {
            displayMode?: string;
            locale?: string;
          };
        }) => void;
        close: () => void;
      };
    };
  }
}

// Trial countdown component
const TrialCountdown: React.FC<{ trialEndDate: string }> = ({ trialEndDate }) => {
  const [timeRemaining, setTimeRemaining] = useState<{
    days: number;
    hours: number;
    minutes: number;
  } | null>(null);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const end = new Date(trialEndDate).getTime();
      const difference = end - now;

      if (difference <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0 });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

      setTimeRemaining({ days, hours, minutes });
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [trialEndDate]);

  if (!timeRemaining) {
    return <span className="text-xs text-gray-500">Calculating...</span>;
  }

  if (timeRemaining.days === 0 && timeRemaining.hours === 0 && timeRemaining.minutes === 0) {
    return <span className="text-xs font-semibold text-red-600">Trial Expired</span>;
  }

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-gray-600">Trial ends in:</span>
      <span className="font-semibold text-orange-500">
        {timeRemaining.days > 0 && `${timeRemaining.days}d `}
        {timeRemaining.hours}h {timeRemaining.minutes}m
      </span>
    </div>
  );
};

// Display order on the subscription page: the "Most Popular" plan first (it's the
// one we want a glancing mobile user to see and tap), then other paid plans, free last.
const rankPlan = (plan: { name: string }): number => {
  if (plan.name === 'business') return 0;
  if (plan.name === 'free') return 2;
  return 1;
};

const SubscriptionPage: React.FC = () => {
  usePageTitle('Subscription');
  const navigate = useNavigate();
  const {
    plans,
    currentSubscription,
    billingHistory,
    loading,
    error,
    fetchPlans,
    fetchCurrentSubscription,
    fetchBillingHistory,
    upgradeSubscription,
    cancelSubscription,
    createPaddleCheckout,
    confirmPaddleCheckout,
  } = useSubscriptionStore();
  const { currentOrganization, fetchCurrentOrganization } = useOrganizationStore();
  const { showSuccess, showError } = useToast();
  const [isCancelling, setIsCancelling] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null); // plan id being processed

  const plansArray = Array.isArray(plans) ? plans : [];

  useEffect(() => {
    fetchPlans();
    fetchBillingHistory();
    fetchCurrentOrganization();
  }, [fetchPlans, fetchBillingHistory, fetchCurrentOrganization]);

  // Fetch subscription for current org (org context ensures we show the correct plan after payment)
  useEffect(() => {
    const orgId = currentOrganization?.id || currentSubscription?.organization?.id;
    fetchCurrentSubscription(orgId);
  }, [fetchCurrentSubscription, currentOrganization?.id, currentSubscription?.organization?.id]);

  const openPaddleCheckout = useCallback(async (planId: string) => {
    setCheckoutLoading(planId);
    try {
      const orgId = currentOrganization?.id || currentSubscription?.organization?.id;
      const checkoutData = await createPaddleCheckout(planId, billingCycle, orgId);

      if (!window.Paddle) {
        throw new Error('Paddle.js not loaded. Please refresh the page and try again.');
      }

      // Set sandbox mode if needed
      if (checkoutData.environment === 'sandbox') {
        window.Paddle.Environment.set('sandbox');
      }

      // Initialize Paddle with client token
      window.Paddle.Initialize({
        token: checkoutData.client_token,
        eventCallback: async (event: any) => {
          if (!event) return;
          if (event.name === 'checkout.completed') {
            const completedTxnId = (event.data && event.data.transaction_id) || checkoutData.transaction_id;
            // Retry confirm - Paddle may need a few seconds to mark transaction as completed
            const confirmWithRetry = async (retries = 3) => {
              for (let i = 0; i < retries; i++) {
                try {
                  await confirmPaddleCheckout(completedTxnId, orgId, billingCycle, planId);
                  return true;
                } catch {
                  if (i < retries - 1) await new Promise((r) => setTimeout(r, 2500));
                  else return false;
                }
              }
              return false;
            };
            try {
              const confirmed = await confirmWithRetry();
              if (confirmed) {
                showSuccess('Payment Successful', 'Your subscription has been activated.');
              } else {
                showSuccess('Payment Received', 'Your subscription is being activated. Please refresh in a few seconds.');
              }
              await fetchCurrentSubscription(orgId);
              await fetchCurrentOrganization(orgId);
              await fetchBillingHistory();
            } catch {
              await fetchCurrentSubscription(orgId);
              await fetchCurrentOrganization(orgId);
              await fetchBillingHistory();
              showSuccess('Payment Received', 'Your subscription is being activated. Please refresh if it does not update.');
            } finally {
              setCheckoutLoading(null);
              window.Paddle?.Checkout?.close?.();
            }
          } else if (event.name === 'checkout.closed') {
            setCheckoutLoading(null);
          } else if (event.name === 'checkout.error') {
            showError('Payment Error', 'An error occurred during checkout. Please try again.');
            setCheckoutLoading(null);
          }
        },
      });

      window.Paddle.Checkout.open({
        transactionId: checkoutData.transaction_id,
        settings: {
          displayMode: 'overlay',
          locale: 'en',
        },
      });
    } catch (err) {
      showError('Checkout Failed', err instanceof Error ? err.message : 'Failed to open payment. Please try again.');
      setCheckoutLoading(null);
    }
  }, [billingCycle, currentOrganization, currentSubscription, createPaddleCheckout, confirmPaddleCheckout, fetchCurrentSubscription, fetchCurrentOrganization, fetchBillingHistory, showSuccess, showError]);

  const handleUpgrade = async (planId: string, planName: string) => {
    const orgId = currentOrganization?.id || currentSubscription?.organization?.id;

    // Free plan: direct downgrade without payment (billing cycle N/A for free)
    if (planName === 'free') {
      try {
        await upgradeSubscription(planId, 'monthly', orgId);
        showSuccess('Plan Changed', 'Your organization has been moved to the Free plan.');
        await fetchCurrentSubscription(orgId);
        await fetchCurrentOrganization(orgId);
      } catch (err) {
        showError('Update Failed', err instanceof Error ? err.message : 'Failed to update subscription.');
      }
      return;
    }

    // Paid plan: open Paddle checkout
    await openPaddleCheckout(planId);
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your organization subscription? You will continue to have access until the end of your billing period.')) {
      return;
    }

    setIsCancelling(true);
    try {
      const orgId = currentOrganization?.id || currentSubscription?.organization?.id;
      await cancelSubscription(orgId);
      showSuccess('Subscription Cancelled', 'Your organization subscription has been cancelled. You will continue to have access until the end of your billing period.');
      await fetchCurrentSubscription(orgId);
      await fetchCurrentOrganization(orgId);
    } catch (err) {
      showError('Cancellation Failed', err instanceof Error ? err.message : 'Failed to cancel subscription.');
    } finally {
      setIsCancelling(false);
    }
  };

  if (loading && !currentSubscription && plansArray.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Subscription</h1>
            <p className="text-sm text-gray-600">Manage your subscription and billing</p>
          </div>
          <button
            onClick={() => navigate('/app/dashboard', { state: { openCreateBoard: true } })}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-xl transition-colors whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Board
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Trial Banner */}
        {currentSubscription && currentSubscription.status === 'trial' && currentSubscription.is_trial_active && (
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">14 Day Trial Active</h3>
                  <p className="text-xs text-gray-600 mt-0.5">
                    You can try all premium features. After the trial, you'll be downgraded to the free plan.
                  </p>
                </div>
              </div>
              {currentSubscription.trial_end_date && (
                <TrialCountdown trialEndDate={currentSubscription.trial_end_date} />
              )}
            </div>
          </div>
        )}

        {/* Trial Expired Banner */}
        {currentSubscription && currentSubscription.status === 'trial' && currentSubscription.is_trial_expired && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-red-900">Trial Expired</h3>
                  <p className="text-xs text-red-700 mt-0.5">
                    Your trial has ended. Choose a plan to continue using premium features.
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  const paidPlans = plansArray.filter(p => p.name !== 'free');
                  if (paidPlans.length > 0) {
                    handleUpgrade(paidPlans[0].id, paidPlans[0].name);
                  }
                }}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors"
              >
                Choose Plan
              </button>
            </div>
          </div>
        )}

        {/* Current Subscription */}
        {currentSubscription && currentSubscription.plan && Number(currentSubscription.plan.monthly_price || 0) === 0 && currentSubscription.status !== 'trial' ? (
          /* Free plan: compact single-line banner — keeps the pricing cards within
             reach on mobile instead of burying them under boards/members/status detail
             a brand-new free user has no reason to study yet. */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-semibold text-gray-900 shrink-0">Free Plan</span>
              <span className="text-xs text-gray-500 truncate">
                {currentSubscription.plan.boards_per_month === null
                  ? 'Unlimited boards'
                  : `${currentSubscription.boards_created_this_month || 0}/${currentSubscription.plan.boards_per_month} boards this month`}
              </span>
            </div>
            <span className="text-xs font-medium text-orange-600 shrink-0">Upgrade below ↓</span>
          </div>
        ) : currentSubscription && currentSubscription.plan ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">Current Plan</h2>
                  {currentSubscription.status === 'trial' && currentSubscription.is_trial_active && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-600 text-xs font-semibold rounded-full">
                      Trial
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {currentSubscription.plan?.display_name || currentSubscription.plan?.name || 'N/A'} Plan
                </p>
              </div>
              <div className="text-right">
                {Number(currentSubscription.plan.monthly_price || 0) === 0 ? (
                  <div className="text-xl font-bold text-gray-900">Free</div>
                ) : currentSubscription.billing_cycle === 'yearly' && Number(currentSubscription.plan.yearly_price || 0) > 0 ? (
                  <>
                    <div className="text-xl font-bold text-gray-900">
                      ${Number(currentSubscription.plan.yearly_price || 0).toFixed(0)}
                    </div>
                    <div className="text-xs text-gray-500">/year</div>
                  </>
                ) : (
                  <>
                    <div className="text-xl font-bold text-gray-900">
                      ${Number(currentSubscription.plan.monthly_price || 0).toFixed(0)}
                    </div>
                    <div className="text-xs text-gray-500">/month</div>
                  </>
                )}
              </div>
            </div>

            {currentSubscription.plan && (
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Boards</p>
                  <p className="text-sm font-medium text-gray-900">
                    {currentSubscription.plan.boards_per_month === null
                      ? 'Unlimited'
                      : `${currentSubscription.boards_created_this_month || 0}/${currentSubscription.plan.boards_per_month} this month`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Members</p>
                  <p className="text-sm font-medium text-gray-900">
                    {currentSubscription.plan.max_users === null
                      ? 'Unlimited'
                      : `${currentSubscription.user_count || 0}/${currentSubscription.plan.max_users}`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">
                    {currentSubscription.status}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
              <div>
                {currentSubscription.status === 'trial' && currentSubscription.trial_end_date && (
                  <p className="text-xs text-gray-500 mb-1">
                    Trial ends: {new Date(currentSubscription.trial_end_date).toLocaleDateString()}
                  </p>
                )}
                {currentSubscription.end_date && currentSubscription.status !== 'trial' && (
                  <p className="text-xs text-gray-500">
                    {currentSubscription.status === 'cancelled'
                      ? `Access until ${new Date(currentSubscription.end_date).toLocaleDateString()}`
                      : `Renews on ${new Date(currentSubscription.end_date).toLocaleDateString()}`}
                  </p>
                )}
              </div>
              {currentSubscription.status === 'active' && currentSubscription.plan.name !== 'free' && (
                <button
                  onClick={handleCancel}
                  disabled={isCancelling}
                  className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                >
                  {isCancelling ? 'Cancelling...' : 'Cancel'}
                </button>
              )}
            </div>
          </div>
        ) : !loading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
            <div className="text-center py-6">
              <p className="text-sm text-gray-600 mb-4">No active subscription found.</p>
              <p className="text-xs text-gray-500 mb-4">
                Please select a plan below to get started.
              </p>
            </div>
          </div>
        )}

        {/* Billing Cycle Toggle */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>Monthly</span>
          <button
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
              billingCycle === 'yearly' ? 'bg-orange-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-400'}`}>
            Yearly
            <span className="ml-1.5 px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full">Save ~17%</span>
          </span>
        </div>

        {/* Available Plans */}
        {plansArray.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <p className="text-gray-600 mb-4">No subscription plans available.</p>
            <button
              onClick={() => {
                const orgId = currentOrganization?.id || currentSubscription?.organization?.id;
                fetchPlans();
                fetchCurrentSubscription(orgId);
              }}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-3 mb-4">
            {/* Mobile stacking order: the "Most Popular" plan leads (it's the one we
                want tapped first, not just whichever plan happens to be cheapest),
                other paid plans follow, free is demoted to a compact card at the end
                since a free user needs the least convincing about it. */}
            {[...plansArray]
              .sort((a, b) => rankPlan(a) - rankPlan(b))
              .map((plan) => {
              const isFreePlan = plan.name === 'free';
              const isSamePlan = currentSubscription?.plan?.id === plan.id;
              const isSameBillingCycle = currentSubscription?.billing_cycle === billingCycle;
              // Free is one plan — billing cycle only applies to paid tiers
              const isCurrentPlan = isSamePlan && (isFreePlan || isSameBillingCycle);
              const canSwitchBillingCycle = isSamePlan && !isSameBillingCycle && !isFreePlan;
              const monthlyPrice = Number(plan.monthly_price || 0);
              const yearlyPrice = Number(plan.yearly_price || 0);
              const displayPrice = billingCycle === 'yearly' && yearlyPrice > 0 ? yearlyPrice / 12 : monthlyPrice;
              const isBusinessPlan = plan.name === 'business';
              const isPaidPlan = plan.name !== 'free';
              const isProcessing = checkoutLoading === plan.id;

              return (
                <div
                  key={plan.id}
                  className={`relative bg-white rounded-lg border-2 ${
                    isCurrentPlan
                      ? 'border-orange-500 shadow-md'
                      : isBusinessPlan
                      ? 'border-orange-300 shadow-sm'
                      : 'border-gray-200 shadow-sm'
                  } ${isFreePlan ? 'p-3' : 'p-4'}`}
                >
                  {isBusinessPlan && !isCurrentPlan && (
                    <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2">
                      <span className="px-2 py-0.5 bg-gradient-to-r from-orange-500 to-amber-600 text-white text-[10px] font-bold rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className={isFreePlan ? 'mb-2' : 'mb-3'}>
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-base font-bold text-gray-900">{plan.display_name}</h3>
                      <div className="text-right">
                        {monthlyPrice === 0 ? (
                          <span className="text-xl font-bold text-gray-900">Free</span>
                        ) : (
                          <>
                            <span className="text-xl font-bold text-gray-900">${displayPrice.toFixed(0)}</span>
                            <span className="text-xs text-gray-500">/mo</span>
                            {billingCycle === 'yearly' && yearlyPrice > 0 && (
                              <div className="text-[10px] text-gray-400">${yearlyPrice}/yr</div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {!isFreePlan && <p className="text-xs text-gray-600">{plan.description}</p>}
                  </div>

                  {isFreePlan ? (
                    /* Compact: boards/members inline, no repeated feature bullets — the
                       user is already on this plan and doesn't need it resold. */
                    <p className="text-xs text-gray-500 mb-2">
                      {plan.boards_per_month === null ? 'Unlimited boards' : `${plan.boards_per_month} boards / month`}
                      {' · '}
                      {plan.max_users === null ? 'Unlimited members' : `Up to ${plan.max_users} members`}
                    </p>
                  ) : (
                    <>
                      <div className="mb-3 pb-3 border-b border-gray-100 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Boards:</span>
                          <span className="text-xs font-semibold text-gray-900">
                            {plan.boards_per_month === null ? 'Unlimited' : `${plan.boards_per_month} / month`}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Members:</span>
                          <span className="text-xs font-semibold text-gray-900">
                            {plan.max_users === null ? 'Unlimited' : `Up to ${plan.max_users}`}
                          </span>
                        </div>
                      </div>

                      {/* Features list */}
                      {plan.features && plan.features.length > 0 && (
                        <div className="mb-3">
                          <ul className="space-y-1">
                            {plan.features.map((feature: string, idx: number) => (
                              <li key={idx} className="flex items-start gap-1.5">
                                <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs text-gray-600">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}

                  <button
                    onClick={() => handleUpgrade(plan.id, plan.name)}
                    disabled={isCurrentPlan || loading || isProcessing || checkoutLoading !== null}
                    className={`w-full py-2 text-center font-semibold text-xs rounded-lg transition-all ${
                      isCurrentPlan
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : isProcessing
                        ? 'bg-orange-400 text-white cursor-wait'
                        : isBusinessPlan && !isCurrentPlan
                        ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white hover:shadow-md disabled:opacity-60'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200 disabled:opacity-60'
                    }`}
                  >
                    {isCurrentPlan
                      ? 'Current Plan'
                      : canSwitchBillingCycle
                      ? billingCycle === 'yearly'
                        ? 'Switch to Yearly'
                        : 'Switch to Monthly'
                      : isProcessing
                      ? 'Opening payment...'
                      : plan.name === 'free'
                      ? 'Downgrade'
                      : isPaidPlan && (currentSubscription?.status === 'trial' && currentSubscription?.is_trial_active)
                      ? 'Upgrade Now'
                      : isPaidPlan
                      ? 'Upgrade'
                      : 'Select'}
                  </button>

                  {/* Payment badge for paid plans */}
                  {isPaidPlan && !isCurrentPlan && (
                    <p className="text-center text-[10px] text-gray-400 mt-1.5">
                      Secured by Paddle
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Enterprise CTA */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4 text-center">
          <h3 className="text-sm font-bold text-gray-900 mb-1">Enterprise</h3>
          <p className="text-xs text-gray-600 mb-3">
            Need SSO or custom integrations? Let's talk.
          </p>
          <a
            href="mailto:admin@retrosive.com"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-xs rounded-lg transition-all"
          >
            Contact Sales
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
        </div>

        {/* Billing History */}
        {billingHistory.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Billing History</h2>
            <div className="space-y-2">
              {billingHistory.map((item) => (
                <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-xs font-medium text-gray-900">
                      {item.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'} Subscription
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(item.payment_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold text-gray-900">
                      ${Number(item.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <span className={`text-xs ${
                      item.status === 'completed' ? 'text-green-600' :
                      item.status === 'failed' ? 'text-red-600' :
                      'text-yellow-600'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;
